import { didLike, ingestEvents, reactionCount } from '@/atoms/stats'
import { trimTagEnd } from '@/lib/draft-event'
import { checkLogin, useCurrentPubkey } from '@/atoms/session'
import * as kinds from '@nostr/tools/kinds'
import client from '@/services/client.service'
import { HugeiconsIcon } from '@hugeicons/react'
import { HeartIcon, Loading03Icon } from '@hugeicons/core-free-icons'
import { Event } from '@nostr/tools/wasm'
import { useAtomValue } from 'jotai'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import Emoji from '../Emoji'
import { formatCount } from './utils'
import { getReplaceableCoordinateFromEvent, isReplaceableEvent } from '@/lib/event'
import { now } from '@/lib/utils'

export default function LikeButton({ event }: { event: Event }) {
  const { t } = useTranslation()
  const pubkey = useCurrentPubkey()
  const [liking, setLiking] = useState(false)
  const did = useAtomValue(didLike(event.id))
  const count = useAtomValue(reactionCount(event.id))

  return (
    <button
      className="flex items-center enabled:hover:text-primary gap-1 px-3 h-full text-muted-foreground"
      title={t('Like')}
      disabled={liking}
      onClick={like}
    >
      {liking ? (
        <HugeiconsIcon icon={Loading03Icon} className="animate-spin" />
      ) : did ? (
        <>
          <Emoji emoji="+" classNames={{ img: 'size-4' }} />
          <div className="text-sm">{formatCount(count)}</div>
        </>
      ) : (
        <>
          <HugeiconsIcon icon={HeartIcon} className="size-4" />
          <div className="text-sm">{formatCount(count)}</div>
        </>
      )}
    </button>
  )

  async function like() {
    checkLogin(async () => {
      if (liking || !pubkey) return

      setLiking(true)
      const timer = setTimeout(() => setLiking(false), 5_000)

      try {
        const tags: string[][] = []
        tags.push(trimTagEnd(['e', event.id, client.getEventHint(event.id), event.pubkey]))
        tags.push(['p', event.pubkey])
        if (event.kind !== kinds.ShortTextNote) {
          tags.push(['k', event.kind.toString()])
        }

        if (isReplaceableEvent(event.kind)) {
          tags.push(
            trimTagEnd([
              'a',
              getReplaceableCoordinateFromEvent(event),
              client.getEventHint(event.id)
            ])
          )
        }

        const seenOn = client.getSeenEventRelayUrls(event.id, event)
        const evt = await client.publish(
          {
            kind: kinds.Reaction,
            content: '+',
            tags,
            created_at: now()
          },
          { additionalRelayUrls: seenOn }
        )
        if (evt) ingestEvents([evt])
      } catch (error) {
        console.error('like failed', error)
      } finally {
        setLiking(false)
        clearTimeout(timer)
      }
    })
  }
}
